import { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useActiveGameStore } from '@/store/activeGameStore';
import { getSocket } from '@/services/socket.service';
import RejoinBanner from './RejoinBanner';

const BG_COLORS: Record<string, string> = {
  default: '',
  linen: '#1c1917',   // stone-900 – warm tinted dark
  slate: '#0f172a',   // slate-900 – cool blue-tinted dark
};

export default function Layout() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const { backgroundTheme } = useSettingsStore();
  const { gameId: activeGameId, clearActive } = useActiveGameStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-clear the active-game entry when the game ends
  // (fires even while the user is on a different page)
  useEffect(() => {
    if (!activeGameId) return;
    const socket = getSocket();
    const handleEnded = () => clearActive();
    socket.on('game:ended', handleEnded);
    return () => { socket.off('game:ended', handleEnded); };
  }, [activeGameId, clearActive]);

  // Show the rejoin banner when there is an active game and we are NOT on that game page
  const onGamePage = location.pathname === `/game/${activeGameId}`;
  const showRejoin = !!activeGameId && !onGamePage;

  // Apply background theme to the page body
  useEffect(() => {
    document.body.style.backgroundColor = BG_COLORS[backgroundTheme] ?? '';
    return () => { document.body.style.backgroundColor = ''; };
  }, [backgroundTheme]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 text-white font-bold text-xl">
              <span className="text-2xl">♟</span>
              <span>ChessPlatform</span>
            </Link>

            {/* Nav Links */}
            <div className="flex items-center gap-6">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm">
                Home
              </Link>
              <Link to="/leaderboard" className="text-gray-300 hover:text-white transition-colors text-sm">
                Leaderboard
              </Link>
              <Link to="/settings" className="text-gray-300 hover:text-white transition-colors text-sm">
                Settings
              </Link>

              {isAuthenticated ? (
                <div className="flex items-center gap-4">
                  <Link
                    to={`/profile/${user?.id}`}
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-1"
                  >
                    <span className="text-blue-400 font-medium">{user?.username}</span>
                    <span className="text-xs text-gray-500">({user?.rating})</span>
                  </Link>
                  <button onClick={handleLogout} className="btn-secondary text-xs px-3 py-1.5">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="text-gray-300 hover:text-white text-sm transition-colors">
                    Login
                  </Link>
                  <Link to="/register" className="btn-primary text-xs px-3 py-1.5">
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Rejoin banner — visible on every page except the active game itself */}
      {showRejoin && <RejoinBanner />}

      {/* Page Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-4 text-center text-gray-500 text-xs">
        ChessPlatform © 2026 — Built with React, Node.js & PostgreSQL
      </footer>
    </div>
  );
}
